import React, { useState, useEffect } from 'react';
import { Table, Button, Input, Space, Card, Tag, Modal, Form, InputNumber, Select, message, Tooltip } from 'antd';
import { PlusOutlined, ReloadOutlined, AimOutlined } from '@ant-design/icons';
import MainLayout from '../../layouts/MainLayout';
import { targetsApi } from '../../utils/api';
import { formatMoneyFa } from '../../utils/formatPriceToman';
import { TableSkeleton } from '../../components/SkeletonLoaders';
import { toJalali } from '../../utils/dateUtils';

const { Option } = Select;

const toPersianNumber = (num) => {
  if (num === null || num === undefined) return '-';
  const persianDigits = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
  return String(num).replace(/\d/g, (d) => persianDigits[parseInt(d)]);
};

const PERSIAN_MONTHS = [
  { value: 1, label: 'فروردین' },
  { value: 2, label: 'اردیبهشت' },
  { value: 3, label: 'خرداد' },
  { value: 4, label: 'تیر' },
  { value: 5, label: 'مرداد' },
  { value: 6, label: 'شهریور' },
  { value: 7, label: 'مهر' },
  { value: 8, label: 'آبان' },
  { value: 9, label: 'آذر' },
  { value: 10, label: 'دی' },
  { value: 11, label: 'بهمن' },
  { value: 12, label: 'اسفند' },
];

const Targets = () => {
  const [loading, setLoading] = useState(false);
  const [targets, setTargets] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [form] = Form.useForm();
  
  let curJalaliYear = 1405;
  let curJalaliMonth = 7;
  try {
    const y = toJalali(new Date(), 'jYYYY');
    const m = toJalali(new Date(), 'jM');
    if (y) curJalaliYear = parseInt(y, 10);
    if (m) curJalaliMonth = parseInt(m, 10);
  } catch (e) {}

  const [filters, setFilters] = useState({
    period_year: curJalaliYear,
    period_month: curJalaliMonth,
  });

  const fetchTargets = async () => {
    setLoading(true);
    try {
      const res = await targetsApi.getAll(filters);
      if (res?.data?.targets) {
        setTargets(Array.isArray(res.data.targets) ? res.data.targets : []);
      } else if (res?.targets) {
        setTargets(Array.isArray(res.targets) ? res.targets : []);
      } else if (Array.isArray(res?.data)) {
        setTargets(Array.isArray(res.data) ? res.data : []);
      }
    } catch (error) {
      console.error('Error fetching targets:', error);
      message.error('خطا در دریافت لیست تارگت‌ها');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTargets();
  }, [filters]);

  const handleCreate = async (values) => {
    setActionLoading(true);
    try {
      await targetsApi.create({
        ...values,
        target_amount: Number(values.target_amount),
        min_threshold_amount: Number(values.min_threshold_amount || 0),
      });
      message.success('تارگت با موفقیت تعیین شد');
      setIsModalVisible(false);
      form.resetFields();
      fetchTargets();
    } catch (error) {
      message.error('خطا در تعیین تارگت');
    } finally {
      setActionLoading(false);
    }
  };

  const columns = [
    {
      title: <span style={{ fontFamily: 'CustomFont' }}>کارمند / دپارتمان</span>,
      key: 'targetable',
      render: (_, record) => (
        <span style={{ fontFamily: 'CustomFont' }}>
          {record.targetable_type === 'user' ? 'کارمند' : 'دپارتمان'} - شناسه: {record.targetable_id}
        </span>
      ),
    },
    {
      title: <span style={{ fontFamily: 'CustomFont' }}>دوره</span>,
      key: 'period',
      render: (_, record) => (
        <span style={{ fontFamily: 'CustomFont' }}>
          {toPersianNumber(record.period_year)}/{toPersianNumber(record.period_month)}
        </span>
      ),
    },
    {
      title: <span style={{ fontFamily: 'CustomFont' }}>مبلغ تارگت</span>,
      dataIndex: 'target_amount',
      key: 'target_amount',
      render: (amount) => (
        <span className="text-blue-600 font-medium" style={{ fontFamily: 'CustomFont' }}>
          {formatMoneyFa(amount)}
        </span>
      ),
    },
    {
      title: <span style={{ fontFamily: 'CustomFont' }}>حداقل آستانه</span>,
      dataIndex: 'min_threshold_amount',
      key: 'min_threshold_amount',
      render: (amount) => (
        <span style={{ fontFamily: 'CustomFont' }}>
          {formatMoneyFa(amount)}
        </span>
      ),
    },
    {
      title: <span style={{ fontFamily: 'CustomFont' }}>درصد تحقق</span>,
      dataIndex: 'achievement_percentage',
      key: 'achievement_percentage',
      render: (percent) => (
        <Tag color={percent >= 100 ? 'success' : percent >= 50 ? 'processing' : 'error'} style={{ fontFamily: 'CustomFont' }}>
          {toPersianNumber(percent || 0)}%
        </Tag>
      ),
    },
  ];

  return (
    <MainLayout>
      <div className="" style={{ fontFamily: 'CustomFont' }}>
        <Card className="shadow-xl border-0" styles={{ body: { padding: 0 } }}>
          <div className="border-b border-gray-200 dark:border-gray-700 p-6 bg-white dark:bg-gray-800 rounded-t-lg">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center">
                  <AimOutlined className="text-blue-600 dark:text-blue-400 text-xl" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-0" style={{ fontFamily: 'CustomFont' }}>
                    مدیریت تارگت‌ها
                  </h1>
                  <p className="text-gray-500 dark:text-gray-400 text-sm m-0">
                    تعیین و پیگیری تارگت‌های فروش
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <Select
                  value={filters.period_year}
                  onChange={(val) => setFilters({ ...filters, period_year: val })}
                  style={{ fontFamily: 'CustomFont', width: 110 }}
                >
                  <Option value={curJalaliYear - 1}>{toPersianNumber(curJalaliYear - 1)}</Option>
                  <Option value={curJalaliYear}>{toPersianNumber(curJalaliYear)}</Option>
                  <Option value={curJalaliYear + 1}>{toPersianNumber(curJalaliYear + 1)}</Option>
                </Select>
                <Select
                  value={filters.period_month}
                  onChange={(val) => setFilters({ ...filters, period_month: val })}
                  style={{ fontFamily: 'CustomFont', width: 120 }}
                >
                  {PERSIAN_MONTHS.map((m) => (
                    <Option key={m.value} value={m.value}>{m.label}</Option>
                  ))}
                </Select>
                <Tooltip title="بروزرسانی">
                  <Button icon={<ReloadOutlined />} onClick={fetchTargets} size="middle" loading={loading} />
                </Tooltip>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => {
                    form.setFieldsValue({
                      period_year: curJalaliYear,
                      period_month: curJalaliMonth,
                    });
                    setIsModalVisible(true);
                  }}
                  size="middle"
                  className="bg-green-600 hover:bg-green-700 border-0"
                  style={{ fontFamily: 'CustomFont' }}
                >
                  تعیین تارگت جدید
                </Button>
              </div>
            </div>
          </div>

          <div className="p-6 bg-gray-50 dark:bg-gray-900/50">
            {loading ? (
              <TableSkeleton rows={5} columns={5} />
            ) : (
              <Card className="dark:bg-gray-800 border-0 shadow-sm">
                <Table
                  columns={columns}
                  dataSource={targets}
                  rowKey="id"
                  pagination={false}
                />
              </Card>
            )}
          </div>
        </Card>
      </div>

      <Modal
        title={<span style={{ fontFamily: 'CustomFont' }}>تعیین تارگت جدید</span>}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
      >
        <Form form={form} layout="vertical" onFinish={handleCreate}>
          <Form.Item name="targetable_type" label={<span style={{ fontFamily: 'CustomFont' }}>نوع تارگت</span>} rules={[{ required: true }]}>
            <Select style={{ fontFamily: 'CustomFont' }}>
              <Option value="user">کارمند</Option>
              <Option value="department">دپارتمان</Option>
            </Select>
          </Form.Item>
          <Form.Item name="targetable_id" label={<span style={{ fontFamily: 'CustomFont' }}>شناسه هدف</span>} rules={[{ required: true }]}>
            <InputNumber className="w-full" style={{ fontFamily: 'CustomFont' }} />
          </Form.Item>
          <Form.Item name="period_year" label={<span style={{ fontFamily: 'CustomFont' }}>سال</span>} rules={[{ required: true }]}>
            <InputNumber className="w-full" style={{ fontFamily: 'CustomFont' }} />
          </Form.Item>
          <Form.Item name="period_month" label={<span style={{ fontFamily: 'CustomFont' }}>ماه</span>} rules={[{ required: true }]}>
            <Select style={{ fontFamily: 'CustomFont' }}>
              {PERSIAN_MONTHS.map((m) => (
                <Option key={m.value} value={m.value}>{m.label}</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="target_amount" label={<span style={{ fontFamily: 'CustomFont' }}>مبلغ تارگت</span>} rules={[{ required: true }]}>
            <InputNumber className="w-full" style={{ fontFamily: 'CustomFont' }} />
          </Form.Item>
          <Form.Item name="min_threshold_amount" label={<span style={{ fontFamily: 'CustomFont' }}>حداقل آستانه</span>}>
            <InputNumber className="w-full" style={{ fontFamily: 'CustomFont' }} />
          </Form.Item>
          <Form.Item name="notes" label={<span style={{ fontFamily: 'CustomFont' }}>توضیحات</span>}>
            <Input.TextArea rows={2} style={{ fontFamily: 'CustomFont' }} />
          </Form.Item>
          <Form.Item className="mb-0 text-left">
            <Space>
              <Button onClick={() => setIsModalVisible(false)} style={{ fontFamily: 'CustomFont' }}>لغو</Button>
              <Button type="primary" htmlType="submit" loading={actionLoading} style={{ fontFamily: 'CustomFont', backgroundColor: '#1890ff' }}>
                ذخیره
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </MainLayout>
  );
};

export default Targets;
